import React, { useCallback, useMemo, useEffect } from 'react';
import { strings } from 'src/localization';
import { ComponentMapIds } from 'src/components/map';
import { IView } from 'Framework';
import css from './Confirmation.module.scss';
import { IconCheckMark } from 'src/content/icons';
import Footer from 'src/components/Footer/Footer';
import { Customer, UpdateApptDateInp, SpecData, ApptVeh, UpdateApptCustInp, DepartmentSpec, VehicleRec, ascPageviewInput, ascCtaInteractionInput } from 'src/models';
import { DateTime } from '@reynolds/datetime';
import { ViewMode } from 'src/enums';
import CalendarLinks from '../CalendarLinks/CalendarLinks';
import { ScrollBar } from '@framework-controls/core';
import { IAppointmentState } from 'src/store/states';
import { convertTimeZeros, convertTo24 } from 'src/helpers/TimeDisplayHelper/TimeDisplayHelper';
import moment from 'moment';
import 'moment/min/locales.min';
import { ascCtaInteraction, ascPageview, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import classNames from 'classnames';

export interface IProps {
    custInfo?: Customer;
    appointment: IAppointmentState;
    vehicle: ApptVeh;
    viewMode: ViewMode;
    specData?: SpecData;
    theme: string;
    specDeptData: DepartmentSpec;
    specProfile: string;
    store: string;
    branch: string;
    locale: string;
    mainPaneViewOrder: IView[];
    vwDeepLinkMileage: string;
    vwProvider: string;
    vwKeyword: string;
    vwVariant: string;
    addlTrackVin: string;
    selectVehicle: (id: string) => void;
    onAppCustUpdate: (apptCust: UpdateApptCustInp) => void;
    pushToMainPanes: (inputView: IView) => void;
    clearMainPanes: () => void;
    clearCustInfo: () => void;
    clearNewVehicle: () => void;
    clearVehicles: () => void;
    clearApptState: () => void;
    clearROState: () => void;
    clearROHistory: () => void;
    clearMenusState: () => void;
    clearAddlTrackCustInfo: () => void;
    waitForLogOff: () => void;
    logCustOff: () => void;
    setViewMode: (viewMode: ViewMode) => void;
    updateApptDate: (inp: UpdateApptDateInp) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    customerSearch: (cust: Customer, profile: string, store: string, branch: string, selectedNum?: string,
        landingPageFlag?: string) => void;
    vehicleList: VehicleRec[],
    setNextVehAppt: (id: string, apptID: string) => void,
    getVehicles: () => void;
    selectedVehicle: string;
    loading: 'idle' | 'pending' | 'succeeded' | 'failed',
    setLoadingStateToIdle: () => void,
    setVinCustReturned: (value: boolean) => void;
    setAddlTrackData: (token: string, vin: string, year: string, make: string, model: string) => void;
}

const Confirmation = (props: IProps) => {
    const dateValue = new DateTime(new Date(props.appointment!['Appointment']?.date));
    // const dayOfWeekLit = dateValue.getDayOfWeek();
    // const MonthLit = dateValue.getMonthOfYear();
    const displayDate = dateValue.format('DD, YYYY');
    const displayTime = props.appointment!['Appointment']?.time;

    const processingDate = dateValue.toDate();
    let processingUTCDate = moment.utc(processingDate);
    processingUTCDate = processingUTCDate.locale(props.locale);
    const dayOfWeekLit = processingUTCDate.format('dddd');
    const MonthLit = processingUTCDate.format('MMMM');


    let apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate + ' | ' + displayTime;
    if (props.appointment.Appointment.transFlag === 'N') {
        apptDate = dayOfWeekLit + ', ' + MonthLit + ' ' + displayDate + '\n' +
        strings.DropOff + displayTime;
    }

    const viewApptList = props.appointment.ViewApptList;
    let newApptVehIndex = '';

    // need to check whether there are any appointments in viewApptList that are sooner than
    // the appointment being added. If so, we won't set this appointment to the next vehicle appointment.
    const closerAppointments = viewApptList ? viewApptList?.filter(appt => {
        const apptDateTime = props.appointment!['Appointment']?.date + ' ' +
            convertTimeZeros(convertTo24(displayTime).toString(), false, true);
        const thisDateTime = appt.date + ' ' + convertTimeZeros(convertTo24(appt.time).toString(), false, false);

        return new Date(Date.parse(thisDateTime)) < new Date(Date.parse(apptDateTime)) && appt.vin === props.vehicle.vin;
    }) : [];

    const newAppt = props.appointment.ViewApptList?.find(
        (appt) => (new Date(appt.date).toString() === new Date(props.appointment!['Appointment']?.date).toString() &&
                    appt.time === displayTime));

    const newApptVehicle = props.vehicleList?.find(veh => veh.vin === props.vehicle.vin);
    if (newApptVehicle) {
        newApptVehIndex = props.vehicleList.indexOf(newApptVehicle!).toString();
    }
    const brandFlags = [
        props.specData?.fordSmarttFlag,
        props.specData?.kiaTrackingFlag,
        // Add more brand tracking flags here in the future
    ];
    const hasAnyBrandFlag = brandFlags.some(Boolean);


    useEffect(() => {
        if (newApptVehIndex !== '' && newAppt && closerAppointments?.length === 0) {
            props.setNextVehAppt(newApptVehIndex, newAppt.apptNum);
        }
    }, [props.setNextVehAppt, newApptVehIndex, closerAppointments?.length, newAppt]);

    const createAcctHandler = useCallback((e: any) => {
        props.clearMainPanes();
        props.clearCustInfo();
        props.clearMenusState();
        props.clearROHistory();
        props.clearVehicles();
        props.clearNewVehicle();
        props.pushToMainPanes({
            viewId: ComponentMapIds.LandingPage,
            component: ComponentMapIds.LandingPage,
        });
        props.pushToMainPanes({
            viewId: ComponentMapIds.SignUp,
            component: ComponentMapIds.SignUp,
        });
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.CreateAcct,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props?.clearCustInfo, props?.clearMainPanes, props?.clearMenusState, props.selectedVehicle, props.vehicleList,
        props?.clearNewVehicle, props?.clearROHistory, props?.clearVehicles, props?.pushToMainPanes]);

    const isVWFlow = Boolean((props.addlTrackVin && props.specData?.vwTrackingFlag === 'Y') && (
        props.vwDeepLinkMileage || props.vwKeyword || props.vwProvider || props.vwVariant
    ));
    const noThanksHandler = useCallback((e: any) => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearCustInfo();
        props.clearMenusState();
        props.clearROHistory();
        props.clearVehicles();
        props.clearNewVehicle();
        props.clearAddlTrackCustInfo();
        props.logCustOff();
        props.setViewMode(ViewMode.LandingPage);
        if (isVWFlow) {
            props.setVinCustReturned(false);
            props.setViewMode(ViewMode.Appt);
            props.pushToMainPanes({
                viewId: ComponentMapIds.CustSearch,
                component: ComponentMapIds.CustSearch,
            });
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.NoThanks,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props?.clearApptState, props?.clearCustInfo, props?.clearMainPanes, props?.clearMenusState, props.selectedVehicle,
        props?.clearNewVehicle, props?.clearROHistory, props?.clearVehicles, props?.setViewMode, props.vehicleList,
        props.clearAddlTrackCustInfo, isVWFlow, props.pushToMainPanes, props.setVinCustReturned, props.logCustOff,
    ]);

    const newAppointmentHandler = useCallback((e: any) => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearCustInfo();
        props.clearMenusState();
        props.clearROHistory();
        props.clearVehicles();
        props.clearNewVehicle();
        props.clearAddlTrackCustInfo();
        props.logCustOff();
        props.setViewMode(ViewMode.Appt);
        props.pushToMainPanes({
            viewId: ComponentMapIds.CustSearch,
            component: ComponentMapIds.CustSearch,
        });
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.NewAppt,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props?.clearApptState, props?.clearCustInfo, props?.clearMainPanes, props?.clearMenusState, props.vehicleList,
        props.selectedVehicle, props?.clearNewVehicle, props?.clearROHistory, props?.clearVehicles, props?.setViewMode,
        props.pushToMainPanes, props.clearAddlTrackCustInfo, props.logCustOff]);

    const viewAcctHandler = useCallback((e: any) => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearMenusState();
        props.clearNewVehicle();
        props.clearROHistory();
        props.selectVehicle('');
        props.setViewMode(ViewMode.CustAcct);
        if (isVWFlow) {
            props.setAddlTrackData('', '', '', '', '');
        }
        if (!props.custInfo?.login) {
            props.pushToMainPanes({
                viewId: ComponentMapIds.LandingPage,
                component: ComponentMapIds.LandingPage,
            });
            props.pushToMainPanes({
                viewId: ComponentMapIds.Login,
                component: ComponentMapIds.Login,
            });
        } else {
            // Need to update the customer state with the new appointment data
            const apptCustInp: UpdateApptCustInp = {
                nameId: props.custInfo!.nameId,
                firstName: props.appointment.ApptCust.custFirstName,
                lastName: props.appointment.ApptCust.custLastName,
                emailAddress: props.appointment.ApptCust.custEmailAddress,
                phoneNumber: props.appointment.ApptCust.custPhoneNumber,
                phoneType: props.custInfo!.phoneType,
                prefContact: props.appointment.ApptCust.custPrefContact,
                loggedIn: props.custInfo!.login,
            };
            props.onAppCustUpdate(apptCustInp);
            const custInp: Customer = {
                nameId: props.custInfo!.nameId,
                firstName: '',
                lastName: '',
                emailAddress: '',
                phoneNumber: '',
                login: true,
                prefContact: '',
                textOptIn: '',
                emailOptIn: '',
                streetAddress: '',
                stateAddress: '',
                city: '',
                zipCode: '',
                addVehNameId: '',
                gmRedeemAmt: '',
                encryptFirst: '',
                encryptLast: '',
                encryptEmail: '',
                encryptPhone: '',
                ascCustId: '',
            };
            // let hideData = false;
            // if (!props.custInfo?.login) {
            //     hideData = true;
            // }
            const selectedNum = props.selectVehicle + '';
            props.customerSearch(custInp, props.specProfile, props.store, props.branch, selectedNum, '');
            props.pushToMainPanes({
                viewId: ComponentMapIds.UserAccountMenu,
                component: ComponentMapIds.UserAccountMenu,
            });
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: strings.ViewAccount,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'nav_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props?.appointment, props?.clearApptState, props?.clearMainPanes, props.customerSearch,
        props?.clearMenusState, props?.clearNewVehicle, props?.clearROHistory, props?.custInfo,
        props?.onAppCustUpdate, props?.pushToMainPanes, props?.selectVehicle, props?.setViewMode,
        props.store, props.branch, props.specProfile, props.vehicleList, props.selectedVehicle, props.setAddlTrackData, isVWFlow]);

    useEffect(() => {
        if (props.loading === 'succeeded') {
            props.setLoadingStateToIdle();

            props.getVehicles();
        }
    }, [props.getVehicles, props.loading, props.setLoadingStateToIdle]);

    const logOffHandler = useCallback((e: any) => {
        props.clearMainPanes();
        props.clearApptState();
        props.clearCustInfo();
        props.waitForLogOff();
        props.logCustOff();
        props.clearMenusState();
        props.clearROHistory();
        props.clearVehicles();
        props.clearNewVehicle();
        props.setViewMode(ViewMode.LandingPage);
        if (isVWFlow) {
            props.setVinCustReturned(false);
            props.setViewMode(ViewMode.Appt);
            props.pushToMainPanes({
                viewId: ComponentMapIds.CustSearch,
                component: ComponentMapIds.CustSearch,
            });
        }
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: props.custInfo?.login ? strings.LogOff : strings.NoThanks,
            elementColor: '',
            elementOrder: '',
            elementType: 'footer',
            elementSubType: 'other_buttons',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props?.clearApptState, props?.clearCustInfo, props?.clearMainPanes, props?.clearMenusState,
        props?.clearNewVehicle, props?.clearROHistory, props?.clearVehicles, props?.setViewMode,
        props.waitForLogOff, props.logCustOff, props.custInfo?.login, props.vehicleList, props.selectedVehicle,
        props.pushToMainPanes, props.setVinCustReturned, isVWFlow,
    ]);

    const createBoxClassName = classNames(
        css.createBox,
        {
            [css.createBoxCV]: (props.theme === 'chevrolet'),
            [css.createBoxHonda]: (props.theme === 'honda'),
            [css.createBoxBuick]: (props.theme === 'buick'),
            [css.createBoxBMW]: (props.theme === 'bmw'),
            [css.createBoxIN]: props.theme === 'infiniti' || props.theme === 'mercedesBenz',
            [css.createBoxNissan]: (props.theme === 'nissan'),
            [css.createBoxMazda]: (props.theme === 'mazda'),
            [css.createBoxAcura]: (props.theme === 'acura'),
            [css.createBoxGenesis]: (props.theme === 'genesis'),
            [css.createBoxLexus]: (props.theme === 'lexus'),
        },
    );

    const IconClassName = classNames(
        css.iconBorder,
        {
            [css.iconBorderCV]: (props.theme === 'chevrolet'),
            [css.iconBorderBuick]: (props.theme === 'buick'),
        },
    );

    const acctConfirm = props.custInfo !== undefined && props.custInfo.login ?
        <div className={createBoxClassName}>
            {strings.ConfirmationViewAccount}
        </div> :
        <div className={createBoxClassName}>
            {hasAnyBrandFlag ?
                strings.AddAnotherAppointment :
                strings.CreateAcctOnConfirm}
        </div>;

    let footer = props.custInfo !== undefined && props.custInfo.hasAccount ?
        <div>
            <Footer
                id='ConfFooter'
                message={strings.ViewAccount}
                handler={viewAcctHandler}
                isDisabled={false}
                secondaryMessage={props.custInfo?.login ? strings.LogOff : strings.NoThanks}
                secondaryhandler={logOffHandler}
                theme={props.theme}
                currentScreen={props.mainPaneViewOrder?.[props.mainPaneViewOrder?.length -1]?.viewId}
            />
        </div> :
        <div>
            <Footer
                id='ConfFooter'
                message={strings.CreateAcct}
                handler={createAcctHandler}
                isDisabled={false}
                secondaryMessage={strings.NoThanks}
                secondaryhandler={noThanksHandler}
                theme={props.theme}
                currentScreen={props.mainPaneViewOrder?.[props.mainPaneViewOrder?.length -1]?.viewId}
            />
        </div>;

    if (hasAnyBrandFlag) {
        footer =
            <div>
                <Footer
                    id='ConfFooter'
                    message={strings.NewAppt}
                    handler={newAppointmentHandler}
                    isDisabled={false}
                    secondaryhandler={() => null}
                    theme={props.theme}
                />
            </div>;
    }

    const defaultMessage =
        <div>
            <div>
                {props.viewMode === ViewMode.CustAcct ? strings.ApptWasUpdated : strings.ApptWasAdded}
            </div>
            <div>
                {strings.CommConfirm}
            </div>
        </div>;

    let confirmationMsg = '';
    if (props.specDeptData.confMessage) {
        confirmationMsg = props.specDeptData.confMessage;
    }

    const calDesc = useMemo(() => {
        if (props.specData && props.specData?.mobileName !== undefined) {
            return strings.CalendarDesc + ' ' + props.specData?.mobileName;
        }

        return strings.UpcomingServiceAppt;
    }, [props.specData]);

    const calTitle = useMemo(() => {
        const vehDesc = props.vehicle.vehYr + ' ' + props.vehicle.make + ' ' + props.vehicle.model;

        return strings.SvcApptFor + ' ' + vehDesc;
    }, [props.vehicle]);

    const calLoc = useMemo(() => {
        const address = props.specData?.svcSpecs?.contactAddr;
        const streetNum = address?.contactStrNum ?? '';
        const street = address?.contactStreet ?? '';
        const city = address?.contactCity ?? '';
        const state = address?.contactState ?? '';
        const zip = address?.contactZip ?? '';

        return `${streetNum} ${street} ${city}, ${state} ${zip}`;
    }, [props.specData]);

    const linkEventHandler = useCallback((e: any, text: string) => {
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: text,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: 'selection_link',
            eventAction: 'click',
            eventActionResult: 'popup',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicleList, props.selectedVehicle);
    }, [props.vehicleList, props.selectedVehicle]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Confirmation', '');
    }, [
        props.addLogEntry,
    ]);

    useEffect(() => {
        if (props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1].viewId === ComponentMapIds.Confirmation) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicleList, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, props.vehicleList, props.selectedVehicle]);


    const congratsDateClass = classNames(
        css.congratsDate,
        {
            [css.congratsDateBMW]: props.theme === 'bmw',
            [css.congratsDateFord]: props.theme === 'ford',
            [css.congratsDateHonda]: props.theme === 'honda',
            [css.congratsDateIN]: props.theme === 'infiniti',
            [css.congratsDateNissan]: props.theme === 'nissan',
            [css.congratsDateAcura]: props.theme === 'acura',
        },
    );
    const congratsLitClass = classNames(
        css.congratsLit,
        {
            [css.congratsLitHonda]: props.theme === 'honda',
            [css.congratsLitMazda]: props.theme === 'mazda',
            [css.congratsLitGenesis]: props.theme === 'genesis',
            [css.textTransformNone]: props.theme === 'infiniti' || props.theme === 'mercedesBenz' || props.theme === 'nissan' ||
                props.theme === 'honda' || props.theme === 'acura',
        },
    );
    const congratsBarClass = classNames(
        css.congratsBar,
        {
            [css.congratsBarBuick]: props.theme === 'buick',
            [css.congratsBarIN]: props.theme === 'infiniti',
            [css.congratsBarAcura]: props.theme === 'acura',
        },
    );

    const addLinkClass = classNames(
        {
            [css.fontSizeBaseSize]: props.theme === 'mercedesBenz',
            [css.MBFontFamilyBase]: props.theme === 'mercedesBenz',
            [css.fontFamilyAcura]: props.theme === 'acura',
        },
    );

    return (
        <div>
            <ScrollBar scrollVertical>
                <div className={css.confirmation}>
                    <div className={props.theme === 'honda' ? css.congratsBoxHonda : css.congratsBox}>
                        <div className={props.theme === 'buick' ? css.congratsTextBuick : css.congratsText}>
                            <div className={IconClassName}>
                                <IconCheckMark className={css.check}/>
                            </div>
                            <h2 className={congratsLitClass}>
                                {strings.Congratulations}
                            </h2>
                        </div>
                        <hr className={congratsBarClass} />
                        <div className={congratsDateClass}>
                            {apptDate}
                        </div>
                        <p className={css.confirmationMessage}>{confirmationMsg ? confirmationMsg : defaultMessage}</p>
                    </div>
                    <div>
                        <h2 className={
                            (props.theme === 'mazda' || props.theme === 'genesis') ? css.saveAppt : undefined
                        }>
                            {strings.SaveAppt}
                        </h2>
                        <div className={css.linkLine}>
                            <h3 className={addLinkClass}>
                                {strings.CalendarAddLink}
                            </h3>
                            <div className={css.linkArea}>
                                <CalendarLinks
                                    date={props.appointment!['Appointment']?.date}
                                    time={props.appointment!['Appointment']?.time}
                                    title={calTitle}
                                    description={calDesc}
                                    location={calLoc}
                                    theme={props.theme}
                                    linkEventHandler={linkEventHandler}
                                    addLogEntry={props.addLogEntry}
                                />
                            </div>
                        </div>
                    </div>
                    {acctConfirm}
                </div>
            </ScrollBar>
            {footer}
        </div >
    );
};

export default Confirmation;